/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useRef, useState } from 'react'

export type UseControlledProps<T> = {
  /**
   * This prop contains the component value when it's controlled.
   * undefined would be uncontrolled, use null if we want controlled
   * but not assign it an actual value
   */
  controlled?: T | null
  /**
   * The default value.
   */
  default: T
  /**
   * The component name displayed in warnings.
   */
  name: string
  state?: string
}

// For explanation of "controlled" and "uncontrolled" concept: https://itnext.io/controlled-and-uncontrolled-component-design-pattern-in-react-21e8d40e46e
export default function useControlled<T>({
  controlled,
  default: defaultProp,
  name,
  state = 'value'
}: UseControlledProps<T>): [
  T,
  (value: T) => void,
  React.MutableRefObject<T | null | undefined>
] {
  const { current: isControlled } = useRef(controlled !== undefined)
  const [valueState, setValue] = useState(defaultProp)
  const value = isControlled ? controlled : valueState
  const valueRef = useRef<T | null | undefined>(value)
  useEffect(() => {
    if (isControlled) {
      valueRef.current = value
    }
  }, [isControlled, value])
  // eslint-disable-next-line no-console
  // console.log(
  //   `useControlled controlled: ${controlled} name: ${name} state: ${state} defaultProp: ${JSON.stringify(
  //     defaultProp
  //   )}`
  // )
  if (process.env.NODE_ENV !== 'production') {
    useEffect(() => {
      if (isControlled !== (controlled !== undefined)) {
        console.error(
          [
            `Tui: a component is changing the ${
              isControlled ? '' : 'un'
            }controlled ${state} state of ${name} to be ${
              isControlled ? 'un' : ''
            }controlled.`,
            'Elements should not switch from uncontrolled to controlled (or vice versa).',
            `Decide between using a controlled or uncontrolled ${name} ` +
              'element for the lifetime of the component.',
            "The nature of the state is determined during the first render, it's considered controlled if the value is not `undefined`.",
            'More info: https://fb.me/react-controlled-components'
          ].join('\n')
        )
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [controlled])

    const { current: defaultValue } = useRef(defaultProp)

    useEffect(() => {
      if (defaultValue !== defaultProp) {
        console.error(
          [
            `Tui: a component is changing the default ${state} state of an uncontrolled ${name} after being initialized. ` +
              `To suppress this warning opt to use a controlled ${name}.`
          ].join('\n')
        )
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(defaultProp)])
  }

  const setValueIfUncontrolled = useCallback(
    (newValue: T) => {
      // eslint-disable-next-line no-console
      // console.log(
      //   `useControlled setValueIfUncontrolled newValue: ${newValue} isControlled: ${isControlled}`
      // )
      if (!isControlled) {
        setValue(newValue)
      }
      valueRef.current = newValue
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [controlled]
  )

  return [value as T, setValueIfUncontrolled as (value: T) => void, valueRef]
}
